import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { listJobs, updateJobStatus } from '../services/jobService';
import { searchFranchisees } from '../services/franchiseeService';
import { searchClients as searchSuppliers } from '../services/clientService'; // Rename import for clarity
import { searchHouseholds as searchAppClients } from '../services/householdService'; // Rename import for clarity
import { resendRCTIEmail } from '../services/rctiService';
import { FaCalendarAlt, FaClock, FaRedo, FaUserTie, FaUser, FaHome, FaMapMarkerAlt, FaDollarSign, FaHourglassHalf, FaTasks, FaEdit, FaLayerGroup, FaHourglassEnd, FaFilter, FaTimes, FaCalendarCheck, FaFileInvoiceDollar, FaStickyNote, FaPencilAlt, FaExclamationTriangle, FaInfoCircle, FaPlus, FaMedkit, FaSort, FaSortUp, FaSortDown, FaEnvelope } from 'react-icons/fa'; // Added FaEnvelope for resend RCTI button
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'; // Added useNavigate, useLocation and useSearchParams
import { debounce } from 'lodash';
import { terminology, allocationTypes, formatters } from '../config/terminology'; // Import terminology and formatters
import { useAppConfig } from '../context/AppConfigContext'; // Import the context hook

const PAGE_SIZE = 20;

// Helper function to format Firestore Timestamp (reuse or move to utils)
const formatTimestamp = (timestamp, includeTime = false) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  const date = new Date(timestamp._seconds * 1000);
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
    options.hour12 = true; // Use AM/PM
  }
  return date.toLocaleDateString('en-AU', options);
};

// Helper to get status object (including color) from code
const getStatusDetails = (statusCode, statusesFromConfig) => {
  const defaultStatus = { code: statusCode, display: statusCode || 'N/A', color: 'gray' };
  if (!statusCode || !statusesFromConfig || statusesFromConfig.length === 0) {
    return defaultStatus;
  }
  const statusObj = statusesFromConfig.find(s => s.code === statusCode);
  // Ensure color is always present, default to gray
  return statusObj ? { ...statusObj, color: statusObj.color || 'gray' } : defaultStatus;
};

// Helper to generate Tailwind classes for status pill
const getStatusPillClasses = (color) => {
  const colorMap = {
    yellow: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    cyan: 'bg-cyan-100 text-cyan-800 border border-cyan-300',
    blue: 'bg-blue-100 text-blue-800 border border-blue-300',
    green: 'bg-green-100 text-green-800 border border-green-300',
    orange: 'bg-orange-100 text-orange-800 border border-orange-300',
    red: 'bg-red-100 text-red-800 border border-red-300',
    gray: 'bg-gray-100 text-gray-800 border border-gray-300',
    gold: 'bg-yellow-300 text-yellow-800 border border-yellow-400',
  };
  return colorMap[color?.toLowerCase()] || colorMap.gray;
};

// Re-use useSearch hook (consider moving to a shared hooks file later)
const useSearch = (searchFunction, options = {}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const optionsString = JSON.stringify(options);

  const debouncedFetch = useCallback(debounce(async (term, fetchOptions) => {
    if (!searchFunction || term.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setIsSearching(true);
    try {
      const data = await searchFunction({ searchString: term, ...fetchOptions });
      const results = data.franchisees || data.clients || data.households || [];
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Search error:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  }, 300), [searchFunction]); // Added searchFunction dependency

  useEffect(() => {
    const currentOptions = JSON.parse(optionsString);
    debouncedFetch(searchTerm, currentOptions);
    return () => debouncedFetch.cancel();
  }, [searchTerm, optionsString, debouncedFetch]);

  return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};

// Get current date at the start (ignoring time for comparison)
const today = new Date();
today.setHours(0, 0, 0, 0);
const todayTimestampSeconds = Math.floor(today.getTime() / 1000);

const JobsListPage = () => {
  const navigate = useNavigate(); // Add useNavigate hook
  const location = useLocation(); // Add useLocation hook

  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);
  const [editingJobId, setEditingJobId] = useState(null);
  const [resendingRCTI, setResendingRCTI] = useState(null); // Track which job's RCTI is being resent
  const [rctiMessages, setRctiMessages] = useState({}); // Track RCTI success/error messages per job { jobId: { type: 'success'|'error', message: '...' } }
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [pageStartAfterIds, setPageStartAfterIds] = useState({ 1: null });
  const [hasNextPage, setHasNextPage] = useState(false);
  const dropdownRef = useRef(null);
  const pageStartAfterIdsRef = useRef(pageStartAfterIds);

  // --- Filter State ---
  // Seeded from the URL so filters survive a reload, a shared link, and browser back/forward.
  // The id params match the API field names: clientId is the Supplier, householdId is the Client.
  const [filterFseId, setFilterFseId] = useState(() => searchParams.get('fseId') || null);
  const [filterClientId, setFilterClientId] = useState(() => searchParams.get('clientId') || null); // This is now Supplier ID
  const [filterHouseholdId, setFilterHouseholdId] = useState(() => searchParams.get('householdId') || null); // This is now Client ID
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('status') || '');
  // Display labels for the id filters — kept in the URL too, otherwise a reload shows a filtered
  // list with empty search boxes.
  const [fseSearchTerm, setFseSearchTerm] = useState(() => searchParams.get('fseName') || '');
  const [clientSearchTerm, setClientSearchTerm] = useState(() => searchParams.get('clientName') || ''); // This is Supplier search term
  const [householdSearchTerm, setHouseholdSearchTerm] = useState(() => searchParams.get('householdName') || ''); // This is Client search term
  // --- End Filter State ---

  // --- Sorting State ---
  const [sortBy, setSortBy] = useState(() => searchParams.get('sortBy') || 'jobDate');
  const [sortDirection, setSortDirection] = useState(() => searchParams.get('sortDir') || 'asc'); // Default: ascending by jobDate
  const sortFieldOptions = [
    { id: 'franchiseeInfo.franchiseName', label: 'Franchisee', defaultDirection: 'asc', valueType: 'text' },
    { id: 'clientInfo.clientName', label: terminology.client.singular, defaultDirection: 'asc', valueType: 'text' },
    { id: 'householdInfo.firstName', label: terminology.household.singular, defaultDirection: 'asc', valueType: 'text' },
    { id: 'jobDate', label: 'Job Date', defaultDirection: 'asc', valueType: 'date' },
  ];

  const directionLabels = {
    asc: 'Ascending',
    desc: 'Descending'
  };

  const getDirectionDescriptor = (valueType, direction) => {
    if (valueType === 'date') {
      return direction === 'asc' ? 'Oldest → Newest' : 'Newest → Oldest';
    }
    return direction === 'asc' ? 'A → Z' : 'Z → A';
  };

  // --- End Sorting State ---

  // --- Search Hooks for Filters ---
  const franchiseeSearch = useSearch(searchFranchisees);
  const supplierSearch = useSearch(searchSuppliers); // Renamed hook instance
  const appClientSearch = useSearch(searchAppClients); // Renamed hook instance
  // --- End Search Hooks ---

  const { loading: configLoading, error: configError, getStatuses } = useAppConfig(); // Use the context hook

  // Memoize statuses from config to prevent unnecessary re-renders
  const jobStatuses = useMemo(() => getStatuses() || [], [getStatuses]);

  useEffect(() => {
    pageStartAfterIdsRef.current = pageStartAfterIds;
  }, [pageStartAfterIds]);

  const fetchJobs = useCallback(async (page, filters = {}) => {
    setLoading(true);
    setError(null);

    const startAfterDocId = page === 1 ? null : pageStartAfterIdsRef.current[page];

    if (page > 1 && startAfterDocId === undefined) {
      console.error("Pagination error: Cannot determine starting point for page", page, ". Available IDs:", pageStartAfterIdsRef.current);
      setError("Pagination error. Please refresh or go back to page 1.");
      setLoading(false);
      return;
    }

    try {
      // Use passed filters or fall back to current state
      const effectiveFilters = {
        fseId: filters.fseId !== undefined ? filters.fseId : filterFseId,
        clientId: filters.clientId !== undefined ? filters.clientId : filterClientId,
        householdId: filters.householdId !== undefined ? filters.householdId : filterHouseholdId,
        status: filters.status !== undefined ? filters.status : filterStatus,
      };

      const options = {
        pageSize,
        startAfterDocId,
        fseId: effectiveFilters.fseId || null,
        clientId: effectiveFilters.clientId || null,
        householdId: effectiveFilters.householdId || null,
        status: effectiveFilters.status || null,
        orderBy: sortBy,
        orderDirection: sortDirection,
      };
      const data = await listJobs(options);
      const fetchedJobs = (data.jobs || []).filter(job => job.isActive !== false);
      setJobs(fetchedJobs);

      const nextDocId = data.nextPageDocId || null;
      setHasNextPage(!!nextDocId);

      if (page === 1) {
        setPageStartAfterIds({ 1: null, ...(nextDocId && { 2: nextDocId }) });
      } else if (nextDocId) {
        setPageStartAfterIds(prev => ({ ...prev, [page + 1]: nextDocId }));
      }

      setCurrentPage(page);

    } catch (err) {
      setError(err.message || 'Failed to load jobs.');
      setJobs([]);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  }, [pageSize, filterFseId, filterClientId, filterHouseholdId, filterStatus, sortBy, sortDirection]); // Updated dependencies

  // Mirror the current filter/sort selection into the query string. Empty values are removed
  // rather than written as blanks, so a clean list has a clean URL.
  const persistFiltersToSearchParams = useCallback((overrides = {}) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const values = {
        fseId: overrides.fseId !== undefined ? overrides.fseId : filterFseId,
        clientId: overrides.clientId !== undefined ? overrides.clientId : filterClientId,
        householdId: overrides.householdId !== undefined ? overrides.householdId : filterHouseholdId,
        status: overrides.status !== undefined ? overrides.status : filterStatus,
        fseName: overrides.fseName !== undefined ? overrides.fseName : fseSearchTerm,
        clientName: overrides.clientName !== undefined ? overrides.clientName : clientSearchTerm,
        householdName: overrides.householdName !== undefined ? overrides.householdName : householdSearchTerm,
        sortBy: overrides.sortBy !== undefined ? overrides.sortBy : sortBy,
        sortDir: overrides.sortDir !== undefined ? overrides.sortDir : sortDirection,
      };
      Object.entries(values).forEach(([key, value]) => {
        if (value === null || value === '' || value === undefined) next.delete(key);
        else next.set(key, value);
      });
      return next;
    }, { replace: true });
  }, [filterFseId, filterClientId, filterHouseholdId, filterStatus, fseSearchTerm, clientSearchTerm, householdSearchTerm, sortBy, sortDirection, setSearchParams]);

  // Fetch once on mount using whatever the URL already carried.
  const didInitialFetch = useRef(false);
  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;
    fetchJobs(1, {
      fseId: filterFseId,
      clientId: filterClientId,
      householdId: filterHouseholdId,
      status: filterStatus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filters auto-apply. Only committed values are watched: the three
  // autocompletes set an id when a suggestion is picked and null it when the box is edited,
  // so the typed text never reaches the API and needs no debounce. Every change resets to
  // page 1, since a cursor from the old result set means nothing against new filters.
  // Guarded by value, not by a "first run" flag: StrictMode double-invokes mount effects, and a
  // boolean would let the second pass through as a real change and refetch.
  const lastAppliedFilters = useRef(null);
  useEffect(() => {
    const applied = JSON.stringify([filterFseId, filterClientId, filterHouseholdId, filterStatus]);
    if (lastAppliedFilters.current === null) {
      lastAppliedFilters.current = applied; // seeded from the URL; the mount fetch already covers it
      return;
    }
    if (lastAppliedFilters.current === applied) return;
    lastAppliedFilters.current = applied;

    persistFiltersToSearchParams();
    fetchJobs(1, {
      fseId: filterFseId,
      clientId: filterClientId,
      householdId: filterHouseholdId,
      status: filterStatus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterFseId, filterClientId, filterHouseholdId, filterStatus]);

  // Filters have already been applied by the effect above by the time this can be clicked, so it
  // re-runs the current query — a manual refresh for a list other admins are changing underneath.
  const handleApplyFilters = () => {
    persistFiltersToSearchParams();
    fetchJobs(1, { fseId: filterFseId, clientId: filterClientId, householdId: filterHouseholdId, status: filterStatus });
  };

  const handleClearFilters = () => {
    setFilterFseId(null);
    setFilterClientId(null);
    setFilterHouseholdId(null);
    setFilterStatus('');
    setFseSearchTerm('');
    setClientSearchTerm('');
    setHouseholdSearchTerm('');
    franchiseeSearch.setSearchTerm('');
    franchiseeSearch.setSuggestions([]);
    supplierSearch.setSearchTerm('');
    supplierSearch.setSuggestions([]);
    appClientSearch.setSearchTerm('');
    appClientSearch.setSuggestions([]);
    // Pass the cleared values explicitly — the setters above have not applied yet. This also
    // covers clearing a half-typed box that never set an id, which the auto-apply effect
    // won't see. The refetch itself is left to that effect.
    persistFiltersToSearchParams({
      fseId: null, clientId: null, householdId: null, status: '',
      fseName: '', clientName: '', householdName: '',
    });
  };

  const createFilterSearchHandlers = (searchState, setFilterId, setSearchTermDisplay) => ({
    onChange: (e) => {
      const value = e.target.value;
      searchState.setSearchTerm(value);
      setSearchTermDisplay(value);
      setFilterId(null);
      if (value.length < 3) searchState.setShowSuggestions(false);
    },
    onSelect: (item) => {
      setFilterId(item.id);
      let displayName;
      if (searchState === franchiseeSearch) {
        const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
        displayName = `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
      } else if (searchState === appClientSearch) {
        displayName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'N/A';
      } else {
        displayName = item.clientName || '';
      }
      setSearchTermDisplay(displayName);
      searchState.setSuggestions([]);
      searchState.setShowSuggestions(false);
    },
    renderSuggestions: (item) => {
      if (searchState === franchiseeSearch) {
        const contactName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
        return `${item.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
      } else if (searchState === appClientSearch) {
        return `${item.firstName || ''} ${item.lastName || ''}`.trim() + (item.addressLine1 ? ` (${item.addressLine1})` : '');
      } else {
        return item.clientName || '';
      }
    }
  });

  const fseFilterHandlers = createFilterSearchHandlers(franchiseeSearch, setFilterFseId, setFseSearchTerm);
  const supplierFilterHandlers = createFilterSearchHandlers(supplierSearch, setFilterClientId, setClientSearchTerm);
  const appClientFilterHandlers = createFilterSearchHandlers(appClientSearch, setFilterHouseholdId, setHouseholdSearchTerm);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const editButtonClicked = event.target.closest('button[aria-label="Edit status"]');
        if (!editButtonClicked) {
          setEditingJobId(null);
        }
      }
    };
    if (editingJobId || franchiseeSearch.showSuggestions || supplierSearch.showSuggestions || appClientSearch.showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingJobId, franchiseeSearch.showSuggestions, supplierSearch.showSuggestions, appClientSearch.showSuggestions]);

  useEffect(() => {
    let timer;
    if (successMessage || updateError) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setUpdateError(null);
        // Clear navigation state to prevent re-applying success message
        navigate(location.pathname, { replace: true, state: {} });
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, updateError, navigate, location.pathname]);

  // Ref to track if initial mount has completed
  const isInitialMount = useRef(true);

  // Trigger data fetch when sorting changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Fetch data with current filters and new sorting
    persistFiltersToSearchParams({ sortBy, sortDir: sortDirection });
    fetchJobs(1, {
      fseId: filterFseId,
      clientId: filterClientId,
      householdId: filterHouseholdId,
      status: filterStatus || null
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDirection]);

  const handleNextPage = () => {
    if (hasNextPage && !loading) {
      fetchJobs(currentPage + 1, { fseId: filterFseId, clientId: filterClientId, householdId: filterHouseholdId, status: filterStatus });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && !loading) {
      fetchJobs(currentPage - 1, { fseId: filterFseId, clientId: filterClientId, householdId: filterHouseholdId, status: filterStatus });
    }
  };

  const handleStatusChange = async (jobId, newStatusValue) => {
    setUpdateError(null);
    setSuccessMessage(null);

    // Prevent direct status change to 'completed' on a weekend (as backend defaults completionDate to today)
    if (newStatusValue === 'completed') {
      const today = new Date();
      const dayOfWeek = today.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        setUpdateError(`Cannot complete job ${jobId}. Completion date cannot fall on a weekend.`);
        setEditingJobId(null);
        return;
      }
    }

    const originalJobs = [...jobs];

    // Find the job being updated to check if it's monthly
    const jobBeingUpdated = jobs.find(job => job.id === jobId);

    setJobs(prevJobs => prevJobs.map(job =>
      job.id === jobId ? { ...job, status: newStatusValue } : job
    ));
    setEditingJobId(null);

    try {
      await updateJobStatus(jobId, newStatusValue);

      // Create success message with additional info for monthly completed jobs
      let message = `Status for job ${jobId} updated successfully.`;
      if (newStatusValue === 'completed' && jobBeingUpdated?.allocationType === 'monthly') {
        message += ' Invoice and RCTI will be generated at the end of the month.';
      }

      setSuccessMessage(message);
    } catch (err) {
      console.error("Failed to update status:", err);
      setUpdateError(`Failed to update status for job ${jobId}. ${err.message}`);
      setJobs(originalJobs);
    }
  };

  // --- Resend RCTI Handler ---
  const handleResendRCTI = async (jobId) => {
    setResendingRCTI(jobId);
    // Clear any existing message for this job
    setRctiMessages(prev => {
      const updated = { ...prev };
      delete updated[jobId];
      return updated;
    });

    try {
      const result = await resendRCTIEmail(jobId);
      
      // Build success message with test mode information
      let message = 'RCTI email resent successfully.';
      
      if (result.data?.testMode) {
        // In test mode - show both intended and actual recipient
        message += ` 🧪 TEST MODE: Email sent to ${result.data.actualRecipient} (intended: ${result.data.recipientEmail})`;
      } else if (result.data?.recipientEmail) {
        // Production mode - show actual recipient
        message += ` Sent to: ${result.data.recipientEmail}`;
      }
      
      // Set success message for this specific job
      setRctiMessages(prev => ({
        ...prev,
        [jobId]: { type: 'success', message }
      }));

      // Auto-clear message after 10 seconds
      setTimeout(() => {
        setRctiMessages(prev => {
          const updated = { ...prev };
          delete updated[jobId];
          return updated;
        });
      }, 10000);
    } catch (err) {
      console.error("Failed to resend RCTI email:", err);
      
      // Set error message for this specific job
      setRctiMessages(prev => ({
        ...prev,
        [jobId]: { type: 'error', message: `Failed to resend RCTI email. ${err.message}` }
      }));

      // Auto-clear error message after 10 seconds
      setTimeout(() => {
        setRctiMessages(prev => {
          const updated = { ...prev };
          delete updated[jobId];
          return updated;
        });
      }, 10000);
    } finally {
      setResendingRCTI(null);
    }
  };

  // --- Sorting Handler ---
    const handleSortChange = (field) => {
      if (sortBy === field) {
        // Toggle direction if clicking same field
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        // Set new field with default direction
        setSortBy(field);
        // Default directions based on field
        if (field === 'jobDate') {
          setSortDirection('asc'); // Oldest first for job date
        } else {
          setSortDirection('asc'); // A-Z for others
        }
      }
      // Reset pagination when sorting changes
      setCurrentPage(1);
      setPageStartAfterIds({ 1: null });
    };

  const handleEditJob = (jobId) => {
    // The query string already holds the filters/sorting, so hand back the route to return to
    // rather than threading a filters object through every downstream screen.
    navigate(`/jobs-list/edit/${jobId}`, {
      state: {
        returnTo: { pathname: '/jobs-list', search: location.search },
        returnLabel: 'Back to Jobs List',
      },
    });
  };

  // --- Sorting Helpers ---
  const getSortIcon = (field) => {
    if (sortBy !== field) return <FaSort className="inline ml-1 text-gray-400" />;
    return sortDirection === 'asc'
      ? <FaSortUp className="inline ml-1 text-white" />
      : <FaSortDown className="inline ml-1 text-white" />;
  };

  const activeSortOption = sortFieldOptions.find(option => option.id === sortBy);
  const activeSortDescriptor = activeSortOption
    ? `${directionLabels[sortDirection]} (${getDirectionDescriptor(activeSortOption.valueType, sortDirection)})`
    : null;
  // --- End Sorting Helpers ---

  if (configLoading) {
    return <p className="text-center text-gray-500 py-10">Loading configuration...</p>;
  }

  if (configError) {
    return <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error loading configuration: {configError}</p>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-jims-cyan-dark">Jobs List</h1>
      </div>

      {/* --- Filter Section --- */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-4 border border-gray-200 filter-section">
        {/* --- Sorting Section (at top of filter card) --- */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
            <FaSort className="mr-2 text-jims-cyan"/>Sort By
          </h2>
         <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleSortChange('franchiseeInfo.franchiseName')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition duration-200 ${
              sortBy === 'franchiseeInfo.franchiseName'
                ? 'bg-jims-cyan text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Franchisee {getSortIcon('franchiseeInfo.franchiseName')}
          </button>
          <button
            onClick={() => handleSortChange('clientInfo.clientName')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition duration-200 ${
              sortBy === 'clientInfo.clientName'
                ? 'bg-jims-cyan text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {terminology.client.singular} {getSortIcon('clientInfo.clientName')}
          </button>
          <button
            onClick={() => handleSortChange('householdInfo.firstName')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition duration-200 ${
              sortBy === 'householdInfo.firstName'
                ? 'bg-jims-cyan text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {terminology.household.singular} {getSortIcon('householdInfo.firstName')}
          </button>
          <button
            onClick={() => handleSortChange('jobDate')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition duration-200 ${
              sortBy === 'jobDate'
                ? 'bg-jims-cyan text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Job Date {getSortIcon('jobDate')}
          </button>
          </div>
          {activeSortOption && activeSortDescriptor && (
            <p className="mt-2 text-xs text-gray-600" role="status">
              Sorted by {activeSortOption.label} · {activeSortDescriptor}
            </p>
          )}
        </div>
        {/* --- End Sorting Section --- */}

        <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center"><FaFilter className="mr-2 text-jims-cyan" />Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          {/* Franchisee Filter */}
          <div className="relative">
            <label htmlFor="fseFilter" className="block text-xs font-medium text-gray-600">Franchisee</label>
            <input
              id="fseFilter"
              type="text"
              autoComplete="off"
              value={fseSearchTerm}
              onChange={fseFilterHandlers.onChange}
              onBlur={() => setTimeout(() => franchiseeSearch.setShowSuggestions(false), 150)}
              onFocus={() => franchiseeSearch.suggestions.length > 0 && franchiseeSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
              className="mt-1 block w-full input-field filter-input border-gray-300"
            />
            {franchiseeSearch.showSuggestions && (
              <ul className="suggestions-list filter-suggestions">
                {franchiseeSearch.isSearching ? <li className="searching">Searching...</li> :
                  franchiseeSearch.suggestions.length > 0 ? franchiseeSearch.suggestions.map(item => (
                    <li key={item.id} className="suggestion-item" onMouseDown={() => fseFilterHandlers.onSelect(item)}>
                      {fseFilterHandlers.renderSuggestions(item)}
                    </li>
                  )) : franchiseeSearch.searchTerm.length >= 3 && <li className="no-results">No franchisees found.</li>}
              </ul>
            )}
          </div>

          {/* Supplier (original Client) Filter */}
          <div className="relative">
            <label htmlFor="clientFilter" className="block text-xs font-medium text-gray-600">{terminology.client.singular}</label>
            <input
              id="clientFilter"
              type="text"
              autoComplete="off"
              value={clientSearchTerm}
              onChange={supplierFilterHandlers.onChange}
              onBlur={() => setTimeout(() => supplierSearch.setShowSuggestions(false), 150)}
              onFocus={() => supplierSearch.suggestions.length > 0 && supplierSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
              className="mt-1 block w-full input-field filter-input border-gray-300"
            />
            {supplierSearch.showSuggestions && (
              <ul className="suggestions-list filter-suggestions">
                {supplierSearch.isSearching ? <li className="searching">Searching...</li> :
                  supplierSearch.suggestions.length > 0 ? supplierSearch.suggestions.map(item => (
                    <li key={item.id} className="suggestion-item" onMouseDown={() => supplierFilterHandlers.onSelect(item)}>
                      {supplierFilterHandlers.renderSuggestions(item)}
                    </li>
                  )) : supplierSearch.searchTerm.length >= 3 && <li className="no-results">No {terminology.client.plural.toLowerCase()} found.</li>}
              </ul>
            )}
          </div>

          {/* Client (formerly Household) Filter */}
          <div className="relative">
            <label htmlFor="householdFilter" className="block text-xs font-medium text-gray-600">{terminology.household.singular}</label>
            <input
              id="householdFilter"
              type="text"
              autoComplete="off"
              value={householdSearchTerm}
              onChange={appClientFilterHandlers.onChange}
              onBlur={() => setTimeout(() => appClientSearch.setShowSuggestions(false), 150)}
              onFocus={() => appClientSearch.suggestions.length > 0 && appClientSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
              className="mt-1 block w-full input-field filter-input border-gray-300"
            />
            {appClientSearch.showSuggestions && (
              <ul className="suggestions-list filter-suggestions">
                {appClientSearch.isSearching ? <li className="searching">Searching...</li> :
                  appClientSearch.suggestions.length > 0 ? appClientSearch.suggestions.map(item => (
                    <li key={item.id} className="suggestion-item" onMouseDown={() => appClientFilterHandlers.onSelect(item)}>
                      {appClientFilterHandlers.renderSuggestions(item)}
                    </li>
                  )) : appClientSearch.searchTerm.length >= 3 && <li className="no-results">No {terminology.household.plural.toLowerCase()} found.</li>}
              </ul>
            )}
          </div>

          {/* Status Filter - UPDATED */}
          <div>
            <label htmlFor="statusFilter" className="block text-xs font-medium text-gray-600">Status</label>
            <select
              id="statusFilter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="mt-1 block w-full input-field filter-input border-gray-300"
              disabled={jobStatuses.length === 0} // Disable if no statuses loaded
            >
              <option value="">All Statuses</option>
              {jobStatuses.map(statusOption => (
                <option key={statusOption.code} value={statusOption.code}>
                  {statusOption.display} {/* Use display name from config */}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            onClick={handleClearFilters}
            className="btn btn-secondary filter-btn inline-flex items-center"
            disabled={loading}
          >
            <FaTimes className="mr-1" /> Clear
          </button>
          <button
            onClick={handleApplyFilters}
            className="btn btn-primary filter-btn inline-flex items-center"
            disabled={loading || franchiseeSearch.isSearching || supplierSearch.isSearching || appClientSearch.isSearching}
          >
            <FaFilter className="mr-1" /> Apply
          </button>
        </div>
      </div>
      {/* --- End Filter Section --- */}

      {loading && <p className="text-center text-gray-500 py-10">Loading jobs...</p>}
      {error && <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error: {error}</p>}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage}
        </div>
      )}
      {updateError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Update Error: {updateError}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="space-y-4">
            {jobs.length === 0 ? (
              <p className="text-center text-gray-500 p-6 bg-white rounded shadow">No jobs found{filterFseId || filterClientId || filterHouseholdId || filterStatus ? ' matching your filters' : ''}.</p>
            ) : (
              jobs.map((job) => {
                // Log allocation data for days/weeks to trace the issue
                if (job.allocationType === 'days' || job.allocationType === 'weeks') {
                  // console.log(`[JobsListPage] Job ${job.id}: allocationType="${job.allocationType}", allocationValue=${job.allocationValue}`);
                }
                
                // Get status details including color
                const statusDetails = getStatusDetails(job.status, jobStatuses);

                // Check if job is overdue
                const isOverdue = job.status === 'approved' &&
                                  job.jobDate?._seconds &&
                                  job.jobDate._seconds < todayTimestampSeconds;

                // Check if job is service-delayed
                const isServiceDelayed = job.status === 'service-delayed';

                return (
                  <div key={job.id} className="bg-white shadow-md rounded-lg p-4 border border-gray-200">
                    {/* --- RCTI Resend Message (Success/Error) --- */}
                    {rctiMessages[job.id] && (
                      <div className={`mb-3 pb-3 border-b ${rctiMessages[job.id].type === 'success' ? 'border-green-200' : 'border-red-200'}`}>
                        <p className={`text-xs flex items-center ${rctiMessages[job.id].type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                          <FaEnvelope className="inline mr-2 flex-shrink-0" />
                          {rctiMessages[job.id].message}
                        </p>
                      </div>
                    )}
                    
                    {/* --- Inactive Job Message --- */}
                    {job.isActive === false && (
                      <div className="mb-3 pb-3 border-b border-orange-200">
                        <p className="text-xs text-orange-600 flex items-center">
                          <FaExclamationTriangle className="inline mr-2 flex-shrink-0" />
                          This job is on hold because the job scheduler is inactive.
                        </p>
                      </div>
                    )}

                    {/* --- Overdue Message --- */}
                    {isOverdue && (
                      <div className="mb-3 pb-3 border-b border-red-200">
                        <p className="text-xs text-red-600 flex items-center">
                          <FaExclamationTriangle className="inline mr-2 flex-shrink-0" />
                          Job is overdue. Contact the franchisee to reschedule or update status to Completed if job was already done.
                        </p>
                      </div>
                    )}

                    {/* Moved: Franchisee/Supplier/Client Info Section */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-3 pb-3 border-b">
                      <div>
                        <p className="font-semibold mb-1">
                          <FaUserTie className="inline mr-1 text-gray-500" />
                          Franchisee {job.franchiseeInfo?.fseCode ? `(${job.franchiseeInfo.fseCode})` : ''}
                        </p>
                        <p>{job.franchiseeInfo?.franchiseName || 'N/A'}</p>
                        <p className="text-xs text-gray-500">
                          {`${job.franchiseeInfo?.firstName || ''} ${job.franchiseeInfo?.lastName || ''}`.trim() || ''}
                        </p>
                      </div>
                      <div>
                        <p className="font-semibold mb-1"><FaUser className="inline mr-1 text-gray-500" />{terminology.client.singular}</p>
                        <p>{job.clientInfo?.clientName || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="font-semibold mb-1"><FaHome className="inline mr-1 text-gray-500" />{terminology.household.singular}</p>
                        <p>{`${job.householdInfo?.firstName || ''} ${job.householdInfo?.lastName || ''}`.trim() || 'N/A'}</p>
                        <p className="text-xs text-gray-500">
                          <FaMapMarkerAlt className="inline mr-1" />
                          {`${job.householdInfo?.addressLine1 || ''}${job.householdInfo?.addressLine2 ? ', ' + job.householdInfo.addressLine2 : ''}, ${job.householdInfo?.state || ''} ${job.householdInfo?.postalCode || ''}`.replace(/^, /, '').replace(/, $/, '').trim() || 'No address'}
                        </p>
                      </div>
                    </div>

                    {/* Status Section (now below entity info) */}
                    <div className="flex justify-between items-start mb-3 pb-3">
                      <div className="text-sm text-gray-700 space-y-1 flex-grow">
                        <div className="relative">
                          <div className="font-semibold flex items-center">
                            <FaRedo className="inline mr-2 text-jims-cyan" />
                            Status:&nbsp;
                            {!editingJobId || editingJobId !== job.id ? (
                              <>
                                {/* UPDATED to use Status Pill */}
                                <span
                                  className={`px-2 py-0.5 inline-block rounded-full text-xs font-medium ${getStatusPillClasses(statusDetails.color)}`}
                                >
                                  {statusDetails.display}
                                </span>
                                {/* Display Xero Invoice Status if applicable */}
                                {(job.status === 'completed' || job.status === 'invoiced') && job.xero_invoice_status && (
                                  <span className="ml-2 px-2 py-0.5 inline-block rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                                    Xero: {job.xero_invoice_status}
                                  </span>
                                )}
                                {/* Duplicate Indicator */}
                                {job.hasDuplicateRCTI && (
                                  <span className="ml-2 px-2 py-0.5 inline-flex items-center rounded-full text-xs font-semibold bg-red-100 text-red-600 border border-red-300">
                                    <FaExclamationTriangle className="mr-1" size="0.7em" />
                                    Duplicate
                                  </span>
                                )}
                                <button
                                  onClick={() => { setEditingJobId(job.id); setUpdateError(null); }}
                                  className="ml-2 text-blue-500 hover:text-blue-700 transition duration-150"
                                  aria-label="Edit status"
                                  disabled={jobStatuses.length === 0} // Disable if no statuses
                                >
                                  <FaEdit size="0.9em" />
                                </button>
                                {/* Resend RCTI Button */}
                                {(job.status === 'completed' || job.status === 'invoiced') && (
                                  <button
                                    onClick={() => handleResendRCTI(job.id)}
                                    className="ml-2 px-2 py-0.5 text-xs bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                    disabled={resendingRCTI === job.id}
                                    aria-label="Resend RCTI"
                                    title="Resend RCTI email to franchisee"
                                  >
                                    {resendingRCTI === job.id ? (
                                      <>
                                        <FaClock className="mr-1 animate-spin" size="0.7em" />
                                        Sending...
                                      </>
                                    ) : (
                                      'Resend RCTI'
                                    )}
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                {/* Show pill even when editing dropdown is open */}
                                <span
                                  className={`px-2 py-0.5 inline-block rounded-full text-xs font-medium ${getStatusPillClasses(statusDetails.color)}`}
                                >
                                  {statusDetails.display}
                                </span>
                                {/* Display Xero Invoice Status if applicable (also when editing dropdown is open) */}
                                {(job.status === 'completed' || job.status === 'invoiced') && job.xero_invoice_status && (
                                  <span className="ml-2 px-2 py-0.5 inline-block rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                                    Xero: {job.xero_invoice_status}
                                  </span>
                                )}
                                {/* Duplicate Indicator (also when editing dropdown is open) */}
                                {job.hasDuplicateRCTI && (
                                  <span className="ml-2 px-2 py-0.5 inline-flex items-center rounded-full text-xs font-semibold bg-red-100 text-red-600 border border-red-300">
                                    <FaExclamationTriangle className="mr-1" size="0.7em" />
                                    Duplicate
                                  </span>
                                )}
                                {/* Resend RCTI Button (also when editing dropdown is open) */}
                                {(job.status === 'completed' || job.status === 'invoiced') && (
                                  <button
                                    onClick={() => handleResendRCTI(job.id)}
                                    className="ml-2 px-2 py-0.5 text-xs bg-jims-cyan text-white rounded hover:bg-jims-cyan-dark transition duration-150 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                                    disabled={resendingRCTI === job.id}
                                    aria-label="Resend RCTI"
                                    title="Resend RCTI email to franchisee"
                                  >
                                    {resendingRCTI === job.id ? (
                                      <>
                                        <FaClock className="mr-1 animate-spin" size="0.7em" />
                                        Sending...
                                      </>
                                    ) : (
                                      'Resend RCTI'
                                    )}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          {editingJobId === job.id && (
                            <div
                              ref={dropdownRef}
                              className="absolute z-10 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200"
                            >
                              <ul className="py-1">
                                {/* UPDATED to use context statuses */}
                                {jobStatuses.map(statusOption => (
                                  <li key={statusOption.code}>
                                    <button
                                      onClick={() => handleStatusChange(job.id, statusOption.code)} // Use code
                                      disabled={job.status === statusOption.code}
                                      className={`block w-full text-left px-4 py-2 text-sm ${
                                        job.status === statusOption.code
                                          ? 'bg-jims-cyan-light text-jims-cyan-dark font-semibold opacity-50 cursor-not-allowed'
                                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                      }`}
                                    >
                                      {statusOption.display} {/* Use display name */}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        {/* Completion date - shown for completed and invoiced jobs */}
                        {(job.status === 'completed' || job.status === 'invoiced') && job.completionDate && (
                          <p className="text-sm text-gray-700">
                            <FaCalendarCheck className="inline mr-1 text-green-600" />
                            Service completed on: <span className="font-bold">{formatTimestamp(job.completionDate)}</span>
                            {job.invoiceCreatedAt && (
                              <span className="ml-3">
                                <FaFileInvoiceDollar className="inline mr-1 text-jims-cyan" />
                                Invoiced on: <span className="font-bold">{formatTimestamp(job.invoiceCreatedAt)}</span>
                              </span>
                            )}
                          </p>
                        )}
                        {/* Invoice and RCTI Links - shown for completed and invoiced jobs */}
                        {(job.status === 'completed' || job.status === 'invoiced') && (
                          <div className="text-sm mt-2">
                            {job.invoiceId || job.rctiId ? (
                              <div className="flex flex-wrap gap-3">
                                {job.invoiceId && (
                                  <Link
                                    to={`/invoices/${job.invoiceId}`}
                                    className="text-jims-cyan hover:text-jims-cyan-dark font-medium inline-flex items-center"
                                  >
                                    <FaFileInvoiceDollar className="mr-1" />
                                    View Invoice {job.invoiceNumber ? `(${job.invoiceNumber})` : ''}
                                  </Link>
                                )}
                                {job.rctiId && (
                                  <Link
                                    to={`/rctis/${job.rctiId}`}
                                    className="text-jims-cyan hover:text-jims-cyan-dark font-medium inline-flex items-center"
                                  >
                                    <FaFileInvoiceDollar className="mr-1" />
                                    View RCTI {job.rctiNumber ? `(${job.rctiNumber})` : ''}
                                  </Link>
                                )}
                              </div>
                            ) : (
                              <p className="text-xs text-orange-600 italic flex items-center">
                                <FaInfoCircle className="mr-1" />
                                There is no invoice and RCTI created for this job
                              </p>
                            )}
                          </div>
                        )}
                        {/* Row 1: Date, Time, Type, Rate */}
                        <p>
                          <span className="mr-3">
                            <FaCalendarAlt className="inline mr-1 text-jims-cyan" />
                            Scheduled for: {formatTimestamp(job.jobDate)}
                          </span>
                          <span className="mr-3">
                            <FaClock className="inline mr-1 text-jims-cyan" />
                            Time: {job.jobTime || 'N/A'}
                          </span>
                          {job.ndisJob && (
                            <span className="mr-3 inline-flex items-center">
                              <FaMedkit className="inline mr-1 text-green-600" />
                              <span className="text-green-600 font-medium">NDIS</span>
                            </span>
                          )}
                          {job.scheduleType && (
                            <span className="mr-3 capitalize">
                              <FaCalendarCheck className="inline mr-1 text-jims-cyan" />
                              Type: {job.scheduleType.replace('-', ' ')}
                              {job.scheduleType === 'recurring' && job.frequencyType && (
                                ` (${formatters.formatFrequency(job.frequencyType, job.frequencyValue)})`
                              )}
                            </span>
                          )}
                          {job.pricePerHour != null && (
                            <span className="mr-3">
                              <FaDollarSign className="inline mr-1 text-jims-cyan" />
                              Rate: ${job.pricePerHour.toFixed(2)} / hr
                            </span>
                          )}

                          {/* Display Invoice Price */}
                           {job.additionalPrice != null && job.additionalPrice > 0 && (
                              <span className="mr-3 ">
                                  <FaPlus className="mr-1 text-jims-cyan inline"  />
                                  Invoice Price (ex. GST): ${job.additionalPrice.toFixed(2)} / hr
                              </span>
                          )}
                          {job.invoicePricePerHour != null && (
                            <span className="mr-3">
                              <FaFileInvoiceDollar className="inline mr-1 text-jims-cyan" />
                              Invoice Rate: ${job.invoicePricePerHour.toFixed(2)} / hr
                            </span>
                          )}
                        </p>
                        {/* Row 2: Combined Allocation and Actual Hours */}
                        <p>
                          <span className="mr-3">
                            <FaLayerGroup className="inline mr-1 text-jims-cyan" />
                            Allocation: {formatters.formatAllocationDetails(job)}
                          </span>
                          <span className="mr-3">
                            <FaHourglassEnd className="inline mr-1 text-jims-cyan" />
                            Actual: {job.actualHours ?? 0} hr(s)
                          </span>
                        </p>
                        {/* Remaining Hours Row */}
                        <p>
                          {job.remainingHours != null && (
                            <span>
                              <FaHourglassEnd className="inline mr-1 text-jims-cyan" />
                              Remaining: {job.remainingHours} hr(s)
                            </span>
                          )}
                        </p>
                        {job.services && job.services.length > 0 && (
                          <p className="text-sm">
                            <FaTasks className="inline mr-2 text-jims-cyan" />
                            Services: {
                              // Check if services is an array of objects with 'name' property
                              Array.isArray(job.services) && job.services.every(s => typeof s === 'object' && s !== null && 'name' in s)
                                ? job.services.map(s => s.name).join(', ')
                                : Array.isArray(job.services) // Fallback for array of strings (old data)
                                  ? job.services.join(', ')
                                  : 'N/A' // Handle unexpected format
                            }
                          </p>
                        )}
                        {job.description && (
                          <p>
                            <FaStickyNote className="inline mr-2 text-jims-cyan" />
                            Description (Invoice): <span className="text-gray-600 italic">{job.description}</span>
                          </p>
                        )}
                        {job.notesForFranchisee && (
                          <p>
                            <FaStickyNote className="inline mr-2 text-jims-cyan" />
                            Notes for FSE: <span className="text-gray-600 italic">{job.notesForFranchisee}</span>
                          </p>
                        )}
                        {job.notesForAdmin && (
                          <p>
                            <FaStickyNote className="inline mr-2 text-jims-cyan" />
                            Notes from franchisee: <span className="text-gray-600 italic">{job.notesForAdmin}</span>
                          </p>
                        )}
                        {job.adminNotes && (
                          <p>
                            <FaStickyNote className="inline mr-2 text-jims-cyan" />
                            Admin Notes: <span className="text-gray-600 italic">{job.adminNotes}</span>
                          </p>
                        )}
                      </div>
                      {/* Edit Job Button */}
                      <div className="flex-shrink-0 ml-4">
                        <button
                          onClick={() => handleEditJob(job.id)}
                          className="text-green-600 hover:text-green-800 transition duration-150 p-1"
                          title="Edit Job Details"
                        >
                          <FaPencilAlt size="1.1em" />
                        </button>
                      </div>
                    </div>
                    {job.remarks && (
                      <div className="mt-2 pt-2 border-t text-xs text-gray-600">
                        <p className="font-semibold mb-1">Remarks:</p>
                        <p>{job.remarks}</p>
                      </div>
                    )}
                    {job.isNoPhotoJob && (
                      <div className="mt-2 pt-2 border-t text-xs text-orange-600 italic">
                        Franchisee declared that there are no photos for this job.
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t text-sm text-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      {job.scheduleId ? (
                        <>
                          <span className="flex items-center text-gray-600">
                            <FaCalendarCheck className="inline mr-2 text-jims-cyan" />
                            Schedule ID: <span className="ml-1 font-semibold text-gray-800">{job.scheduleId}</span>
                          </span>
                          <Link
                            to={`/job-scheduler/edit/${job.scheduleId}`}
                            className="inline-flex items-center text-jims-cyan hover:text-jims-cyan-dark font-medium"
                          >
                            View Job Schedule
                          </Link>
                        </>
                      ) : (
                        <span className="text-gray-500">No schedule linked to this job.</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="mt-6 flex justify-center items-center space-x-4">
            <button
              onClick={handlePrevPage}
              disabled={currentPage <= 1 || loading}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-400 transition duration-300"
            >
              Previous
            </button>
            <span className="text-gray-700">Page {currentPage}</span>
            <button
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
              className="px-4 py-2 bg-jims-cyan text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-jims-cyan-dark transition duration-300"
            >
              Next
            </button>
          </div>
        </>
      )}
      <style>{`
        .input-field {
          padding: 0.5rem 0.75rem;
          border-width: 1px;
          border-radius: 0.375rem;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          outline: none;
          transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        }
        .input-field:focus {
          border-color: #06B6D4;
          box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.3);
        }
        .suggestions-list {
          position: absolute;
          z-index: 10;
          width: 100%;
          background-color: white;
          border: 1px solid #D1D5DB;
          border-radius: 0.375rem;
          margin-top: 0.25rem;
          max-height: 15rem;
          overflow-y: auto;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        .suggestion-item {
          padding: 0.5rem 0.75rem;
          cursor: pointer;
        }
        .suggestion-item:hover {
          background-color: #67E8F9;
        }
        .searching, .no-results {
          padding: 0.5rem 0.75rem;
          color: #6B7280;
        }
        .btn {
          padding: 0.5rem 1rem;
          font-weight: 700;
          border-radius: 0.375rem;
          transition: background-color 0.3s ease, opacity 0.3s ease;
          border: 1px solid transparent;
          cursor: pointer;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default JobsListPage;
